import React from 'react'
import { Box, Image } from '@moonpig/launchpad-components'
import { styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import table from './assets/table.png'
import { StyledCardImageWrapper } from './StyledCardImage'
import { CardImageOrientationWrapper } from './CardImageOrientationWrapper'
import { useLocaleText } from './locale'
import { CardSize, Orientation, Variant } from './types'
import {
  getCardScaleValue,
  getCardWidth,
} from './utils/getCardDimensionDetails'
import { useProductStore } from '../../contexts/product'
import { getCardOrientation } from './utils/getCardOrientation'
import { getCardVariantName } from './utils/getCardVariantName'
import { getDimensions } from './utils/getDimensions'

const StyledImg = styled.img`
  width: 91%;
  transform: translateZ(0);
`

const StyledContainer = styled(Box)`
  ${s({
    width: 1,
    textAlign: 'center',
    position: 'relative',
  })}
`

const calculatePercentageScaleRelativeToA4 = ({
  variant,
  orientation,
}: {
  variant: Variant
  orientation: Orientation
}): string => {
  const { dimensions } = variant
  const cardWidth = getCardWidth({ dimensions, orientation })
  const cardScale = getCardScaleValue({ cardWidth, orientation })
  const percentageScale = (cardScale * 100).toFixed(0)

  return `${percentageScale}%`
}

const StyledBgImage = styled(Image)`
  ${s({
    display: 'block',
    width: 1,
  })}
`

const PreviewContent: React.FC<{
  cardOrientation: Orientation
  selectedVariant: Variant
  coverImageUrl: string
}> = ({ cardOrientation, selectedVariant, coverImageUrl }) => {
  const getLocaleText = useLocaleText()
  const variantName = getLocaleText(
    `card_scene_preview.standard_size`,
  ).toLowerCase()

  const variantImageAltTag = getLocaleText('card_scene_preview.card_alt_text', {
    variant: variantName,
    percentageScale: calculatePercentageScaleRelativeToA4({
      variant: selectedVariant,
      orientation: cardOrientation,
    }),
  })
  return (
    <StyledContainer data-testid="product-size-preview">
      <StyledBgImage
        src={table}
        alt={getLocaleText('card_scene_preview.image_alt_text')}
        aria-hidden
      />
      <StyledCardImageWrapper
        variant={selectedVariant}
        orientation={cardOrientation}
      >
        <CardImageOrientationWrapper orientation={cardOrientation}>
          <StyledImg src={coverImageUrl} alt={variantImageAltTag} />
        </CardImageOrientationWrapper>
      </StyledCardImageWrapper>
    </StyledContainer>
  )
}

export const CardScenePreview: React.FC<{
  coverImageUrl: string
}> = ({ coverImageUrl }) => {
  const [{ product }] = useProductStore()

  const variants: Variant[] = product.variants.map(listVariant => {
    return {
      name: getCardVariantName(listVariant.key),
      title: listVariant.title,
      dimensions: getDimensions(listVariant.dimensions),
      sku: listVariant.sku,
      key: listVariant.key,
    }
  })

  const currentVariant = variants.find(
    ({ name }) => name === CardSize.Standard,
  ) as Variant

  const CardPreviewProps = {
    selectedVariant: currentVariant,
    cardOrientation: getCardOrientation({
      isLandscape: !!product.isLandscape,
      variantKey: product.masterVariant.key,
    }),
    coverImageUrl,
  }

  return <PreviewContent {...CardPreviewProps} />
}
